<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouterLink, RouterView, useRoute } from 'vue-router'
import DiscoveryToast from '@/components/DiscoveryToast.vue'
import InstallNudge from '@/components/InstallNudge.vue'
import SecretCreature from '@/components/SecretCreature.vue'
import { useDiscoveries } from '@/composables/useDiscoveries'
import LanguageSwitcher from '@/components/LanguageSwitcher.vue'
import TabIcon from '@/components/TabIcon.vue'
import { theme, setTheme, THEMES, type Theme } from '@/composables/useTheme'
import { useInstallPrompt } from '@/composables/useInstallPrompt'
import { useAuth } from '@/composables/useAuth'
import { useProfile } from '@/composables/useProfile'
import { useOnline } from '@/composables/useOnline'
import { prefetchForOffline } from '@/lib/prefetch'
import { LOGOUT_URL, isAdmin, userName, markSeen, logout } from '@/api/auth'

const { online } = useOnline()
const { installable, promptInstall } = useInstallPrompt()
const { principal, isAuthenticated, load: loadAuth } = useAuth()
const { profile, load: loadProfile } = useProfile()

function onThemeChange(e: Event) {
  setTheme((e.target as HTMLSelectElement).value as Theme)
}

// Burger-menu: samler alle header-handlinger i én dropdown, saa toppen fylder mindre.
const menuOpen = ref(false)
const toggleMenu = () => (menuOpen.value = !menuOpen.value)
const closeMenu = () => (menuOpen.value = false)
function doInstall() {
  promptInstall()
  closeMenu()
}
function onDocClick(e: MouseEvent) {
  if (menuOpen.value && !(e.target as HTMLElement).closest('.menu-wrap')) closeMenu()
}
function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') closeMenu()
}

// Prefer the saved profile name; fall back to the login claim.
const displayName = computed(
  () => profile.value?.fullName || (principal.value ? userName(principal.value) : ''),
)

// Admin link is shown to administrators (and in local dev). Access is enforced
// server-side by Static Web Apps' role rules regardless.
const showAdmin = computed(() => import.meta.env.DEV || isAdmin(principal.value))

// Reset the home-screen icon badge whenever the user opens/foregrounds the app, so the
// counter only reflects notifications received since they last opened it. The SW owns the
// persisted count, so tell it to reset too.
function clearAppBadge() {
  const nav = navigator as unknown as { clearAppBadge?: () => Promise<void> }
  void nav.clearAppBadge?.().catch(() => {})
  void navigator.serviceWorker?.ready
    .then((reg) => reg.active?.postMessage({ type: 'clear-badge' }))
    .catch(() => {})
}

function onVisibilityChange() {
  if (document.visibilityState === 'visible') {
    clearAppBadge()
  } else {
    // App lukkes/går i baggrunden → gem data ned så den kan bruges offline.
    void prefetchForOffline()
  }
}

// Load (or refresh) the signed-in user. force=true re-fetches even if already loaded —
// used on reconnect, since auth/profile are otherwise load-once. Without this, an app that
// first loaded offline (principal=null) would never see roles again after coming back
// online, so the admin menu item stays hidden and /admin bounces to /login.
async function loadUser(force = false) {
  await loadAuth(force)
  if (isAuthenticated.value && principal.value) {
    void markSeen(principal.value)
    await loadProfile(force)
  }
}

// Re-resolve auth/roles when connectivity returns (mirrors the SWR reconnect refresh in
// useCached), so the admin menu item reappears and works after being offline.
watch(online, (isOnline, was) => {
  if (isOnline && !was) void loadUser(true)
})

// ---- Skjulte detaljer -------------------------------------------------------------------
// Tre smaa ting bor her, fordi de skal kunne ske hvor som helst i appen: den sjaeldne haj,
// kuglefisken i logoet og vanddraaberne ved hurtig scroll.

const { discover, load: loadDiscoveries } = useDiscoveries()

// Hajen. Saa sjaelden at hverken du eller jeg ser den i test — derfor ?spot=whiteshark, som
// tvinger den frem. Ellers ét slag pr. 6.000 sidevisninger, afgjort ved hvert rutesskift.
const HAJ_CHANCE = 1 / 6000
const hajFremme = ref(false)
const route = useRoute()
watch(() => route.fullPath, () => {
  if (hajFremme.value) return
  const tvunget = new URLSearchParams(location.search).get('spot') === 'whiteshark'
  if (tvunget || Math.random() < HAJ_CHANCE) hajFremme.value = true
}, { immediate: true })

// Kuglefisken: tyve klik paa logoet indenfor fem sekunder. Taelleren nulstilles af pausen,
// saa almindelig navigation frem og tilbage aldrig udloeser den.
const logoKlik = ref(0)
let logoTimer: ReturnType<typeof setTimeout> | undefined
const puster = ref(false)
function onBrandClick() {
  logoKlik.value += 1
  clearTimeout(logoTimer)
  logoTimer = setTimeout(() => { logoKlik.value = 0 }, 5000)
  if (logoKlik.value >= 20) {
    logoKlik.value = 0
    puster.value = true
    setTimeout(() => { puster.value = false }, 1400)
    void discover('pufferfish', isAuthenticated.value)
  }
}

// Vanddraaber: kun ved rigtig hurtig scroll, som naar man haiver sig gennem listen. Ingen
// tilstand, ingen samling — det er ren stemning, som en maske der blev oversproejtet.
const draaber = ref<{ id: number; left: number; delay: number }[]>([])
let sidsteY = 0
let sidsteT = 0
let draabeId = 0
function onScroll() {
  const nu = performance.now()
  const y = window.scrollY
  const dt = nu - sidsteT
  if (dt > 0 && dt < 120) {
    const fart = Math.abs(y - sidsteY) / dt // px pr. ms
    if (fart > 4 && draaber.value.length === 0) {
      draaber.value = Array.from({ length: 4 }, (_, i) => ({
        id: draabeId++, left: 12 + Math.random() * 76, delay: i * 90,
      }))
      setTimeout(() => { draaber.value = [] }, 1300)
    }
  }
  sidsteY = y
  sidsteT = nu
}

onMounted(async () => {
  clearAppBadge()
  document.addEventListener('visibilitychange', onVisibilityChange)
  document.addEventListener('click', onDocClick)
  document.addEventListener('keydown', onKey)
  window.addEventListener('scroll', onScroll, { passive: true })
  await loadUser()
  void loadDiscoveries(isAuthenticated.value)
  // App startet → gem data ned til offline-brug (best-effort, kun online).
  void prefetchForOffline()
})
onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', onVisibilityChange)
  document.removeEventListener('click', onDocClick)
  document.removeEventListener('keydown', onKey)
  window.removeEventListener('scroll', onScroll)
})
</script>

<template>
  <div class="app-shell">
    <header class="app-header">
      <RouterLink class="brand" to="/" @click="closeMenu(); onBrandClick()">
        <span class="brand-mark" :class="{ puff: puster }" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <!-- Boelgerne sidder x=2..19,5 i et 24-felt; translate centrerer dem i cirklen. -->
            <g transform="translate(1.25 0)">
              <path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 1.3 0 1.9-.5 2.5-1" />
              <path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 1.3 0 1.9-.5 2.5-1" />
              <path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 1.3 0 1.9-.5 2.5-1" />
            </g>
          </svg>
        </span>
        <span class="brand-name">DiveSiteForecast</span>
      </RouterLink>
      <div class="menu-wrap">
        <button
          class="burger"
          type="button"
          :class="{ open: menuOpen }"
          :aria-expanded="menuOpen"
          aria-controls="main-menu"
          :aria-label="$t('nav.menu')"
          @click.stop="toggleMenu"
        >
          <span></span><span></span><span></span>
        </button>
        <transition name="menu-fade">
          <div v-if="menuOpen" id="main-menu" class="menu" role="menu">
            <RouterLink to="/" class="menu-item" role="menuitem" @click="closeMenu">{{ $t('nav.places') }}</RouterLink>
            <RouterLink v-if="isAuthenticated" to="/favorites" class="menu-item" role="menuitem" @click="closeMenu">{{ $t('nav.favorites') }}</RouterLink>
            <!-- Admin er nu flere sider. Gruppen giver dem hver sit menupunkt, saa man kan
                 gaa direkte til fx Sigtmeldinger uden at gaa via oversigten. -->
            <template v-if="showAdmin">
              <div class="menu-group-label">{{ $t('nav.admin') }}</div>
              <!-- /admin er praefiks for alle de andre, saa den inklusive router-link-active
                   ville markere Oversigt paa hver eneste admin-side. Kun praecist match her. -->
              <RouterLink to="/admin" class="menu-item sub" role="menuitem" active-class="is-prefix" exact-active-class="router-link-active" @click="closeMenu">{{ $t('adminNav.overview') }}</RouterLink>
              <RouterLink to="/admin/dykkersteder" class="menu-item sub" role="menuitem" @click="closeMenu">{{ $t('adminNav.sites') }}</RouterLink>
              <RouterLink to="/admin/brugere" class="menu-item sub" role="menuitem" @click="closeMenu">{{ $t('adminNav.users') }}</RouterLink>
              <RouterLink to="/admin/sigtmeldinger" class="menu-item sub" role="menuitem" @click="closeMenu">{{ $t('adminNav.reports') }}</RouterLink>
              <RouterLink to="/admin/opdagelser" class="menu-item sub" role="menuitem" @click="closeMenu">{{ $t('adminNav.discoveries') }}</RouterLink>
              <RouterLink to="/admin/aktivitet" class="menu-item sub" role="menuitem" @click="closeMenu">{{ $t('adminNav.activity') }}</RouterLink>
              <RouterLink to="/admin/statistik" class="menu-item sub" role="menuitem" @click="closeMenu">{{ $t('adminNav.stats') }}</RouterLink>
              <div class="menu-sep"></div>
            </template>
            <RouterLink to="/settings" class="menu-item" role="menuitem" @click="closeMenu">{{ $t('settings.title') }}</RouterLink>
            <RouterLink to="/about" class="menu-item" role="menuitem" @click="closeMenu">{{ $t('nav.about') }}</RouterLink>
            <RouterLink v-if="isAuthenticated" to="/profile" class="menu-item" role="menuitem" @click="closeMenu">{{ displayName || $t('nav.profile') }}</RouterLink>
            <RouterLink v-else to="/login" class="menu-item" role="menuitem" @click="closeMenu">{{ $t('common.login') }}</RouterLink>
            <div class="menu-sep"></div>
            <div class="menu-row">
              <span class="menu-label">{{ $t('nav.language') }}</span>
              <LanguageSwitcher />
            </div>
            <div class="menu-row">
              <span class="menu-label">{{ $t('theme.label') }}</span>
              <select class="theme-select" :value="theme" :aria-label="$t('theme.label')" @change="onThemeChange">
                <option v-for="tm in THEMES" :key="tm" :value="tm">{{ $t(`theme.${tm}`) }}</option>
              </select>
            </div>
            <button v-if="installable" class="menu-item mi-button" type="button" @click="doInstall">{{ $t('nav.install') }}</button>
            <a v-if="isAuthenticated" :href="LOGOUT_URL" class="menu-item" role="menuitem" @click.prevent="closeMenu(); logout()">{{ $t('common.logout') }}</a>
          </div>
        </transition>
      </div>
    </header>

    <transition name="offline-slide">
      <div v-if="!online" class="offline-bar" role="status">
        <span class="offline-dot" aria-hidden="true"></span>{{ $t('offline.banner') }}
      </div>
    </transition>

    <main class="app-main">
      <RouterView />
    </main>

    <footer class="app-footer">
      <small>{{ $t('app.attribution') }}</small>
    </footer>

    <!-- Skjulte detaljer: hajen, draaberne og fund-beskeden ligger over alt andet. -->
    <SecretCreature
      v-if="hajFremme"
      species-id="whiteshark"
      @caught="discover('whiteshark', isAuthenticated); hajFremme = false"
      @gone="hajFremme = false"
    />
    <div v-if="draaber.length" class="draaber" aria-hidden="true">
      <span
        v-for="d in draaber"
        :key="d.id"
        class="draabe"
        :style="{ left: d.left + '%', animationDelay: d.delay + 'ms' }"
      ></span>
    </div>
    <DiscoveryToast />
    <InstallNudge />

    <nav class="tabbar">
      <RouterLink to="/" class="tab"><TabIcon name="waves" /><span>{{ $t('nav.places') }}</span></RouterLink>
      <RouterLink v-if="isAuthenticated" to="/favorites" class="tab"><TabIcon name="star" /><span>{{ $t('nav.favorites') }}</span></RouterLink>
      <RouterLink v-if="showAdmin" to="/admin" class="tab"><TabIcon name="wrench" /><span>{{ $t('nav.admin') }}</span></RouterLink>
      <RouterLink v-if="isAuthenticated" to="/profile" class="tab"><TabIcon name="user" /><span>{{ $t('nav.profile') }}</span></RouterLink>
      <RouterLink v-else to="/login" class="tab"><TabIcon name="user" /><span>{{ $t('common.login') }}</span></RouterLink>
    </nav>
  </div>
</template>

<style scoped>
/* Offline-indikator: tydelig, men diskret bjælke under headeren. */
.offline-bar {
  display: flex; align-items: center; justify-content: center; gap: 0.5rem;
  padding: 0.45rem 0.9rem; font-size: 0.85rem; font-weight: 600;
  background: color-mix(in srgb, var(--warn) 22%, var(--bg)); color: var(--text);
  border-bottom: 1px solid color-mix(in srgb, var(--warn) 45%, transparent);
  /* Forsidens hero traekker sig op under headeren og er position: relative — uden et
     stablingslag her ville den male hen over offline-bjælken. Under headerens 1100. */
  position: relative; z-index: 2;
}
.offline-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--warn); flex: 0 0 auto; }
.offline-slide-enter-active, .offline-slide-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.offline-slide-enter-from, .offline-slide-leave-to { opacity: 0; transform: translateY(-100%); }

.menu-wrap { position: relative; display: flex; }

.burger {
  display: inline-flex; flex-direction: column; justify-content: center; gap: 4px;
  width: 40px; height: 40px; padding: 10px; border-radius: 10px;
  border: 1px solid var(--border); background: var(--surface-2); color: var(--text); cursor: pointer;
}
.burger span { display: block; height: 2px; width: 100%; border-radius: 2px; background: currentColor; transition: transform 0.2s, opacity 0.2s; }
.burger.open span:nth-child(1) { transform: translateY(6px) rotate(45deg); }
.burger.open span:nth-child(2) { opacity: 0; }
.burger.open span:nth-child(3) { transform: translateY(-6px) rotate(-45deg); }
.burger:focus-visible { outline: 2px solid var(--accent); }

.menu {
  position: absolute; right: 0; top: calc(100% + 10px); z-index: 30;
  min-width: 15rem; max-height: 80vh; overflow-y: auto;
  display: flex; flex-direction: column; gap: 0.1rem; padding: 0.5rem;
  background: var(--bg); border: 1px solid var(--border); border-radius: 14px;
  box-shadow: var(--shadow);
}
.menu-item {
  display: flex; align-items: center; gap: 0.6rem;
  padding: 0.6rem 0.7rem; border-radius: 10px; text-decoration: none;
  color: var(--text); font-weight: 600; font-size: 0.95rem;
  background: transparent; border: 0; width: 100%; text-align: left; cursor: pointer; font: inherit;
}
.menu-item:hover { background: var(--surface-2); }
.menu-item.router-link-active { color: var(--accent); }
.mi-button { font-weight: 600; }
.menu-sep { height: 1px; background: var(--border); margin: 0.35rem 0.2rem; }
.menu-group-label {
  padding: 0.5rem 0.7rem 0.15rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
}
/* Indrykket, saa gruppen laeses som en gruppe og ikke som seks sidestillede topniveau-punkter. */
.menu-item.sub { padding-left: 1.4rem; }
.menu-row { display: flex; align-items: center; justify-content: space-between; gap: 0.6rem; padding: 0.4rem 0.7rem; }
.menu-label { color: var(--muted); font-size: 0.9rem; }

.menu-fade-enter-active, .menu-fade-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.menu-fade-enter-from, .menu-fade-leave-to { opacity: 0; transform: translateY(-6px); }
/* ---- Skjulte detaljer ----
   Kuglefisken: logoet puster sig op og falder tilbage. Ingen tekst, ingen forklaring. */
.brand-mark.puff { animation: puf 1.4s ease-in-out; }
@keyframes puf {
  0%, 100% { transform: scale(1); }
  35% { transform: scale(1.45) rotate(-4deg); }
  60% { transform: scale(1.35) rotate(4deg); }
}

/* Vanddraaber ved hurtig scroll — som en maske der blev oversproejtet. */
.draaber { position: fixed; inset: 0; z-index: 55; pointer-events: none; }
.draabe {
  position: absolute;
  top: -2rem;
  width: 0.5rem;
  height: 0.7rem;
  border-radius: 60% 60% 50% 50% / 70% 70% 40% 40%;
  background: color-mix(in srgb, var(--accent) 35%, transparent);
  box-shadow: inset 0 -2px 3px color-mix(in srgb, #fff 30%, transparent);
  animation: draabe-fald 1.2s ease-in forwards;
}
@keyframes draabe-fald {
  from { transform: translateY(0) scaleY(0.8); opacity: 0; }
  15% { opacity: 0.85; }
  to { transform: translateY(48vh) scaleY(1.4); opacity: 0; }
}
</style>
